import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useResource } from "@/hooks/useResource";
import { Loader2, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";

function DynamicSelect({ field, value, onChange }) {
  const { data } = useResource(field.optionsEndpoint, { enabled: !!field.optionsEndpoint });
  const options = field.options
    ? field.options
    : (Array.isArray(data) ? data : []).map((o) => ({ value: o[field.optionValue], label: o[field.optionLabel] }));
  return (
    <Select value={value ?? ""} onValueChange={onChange}>
      <SelectTrigger data-testid={`field-${field.name}`} className="h-9 text-sm">
        <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
      </SelectTrigger>
      <SelectContent className="bg-white">
        {options.map((o) => (
          <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// "Select Existing Parent Company" picker: pick an existing parent (links
// parentCompanyId + authoritative name) or choose "+ New parent company" and type one.
function ParentCompanyField({ field, values, setField }) {
  const { data } = useResource(field.optionsEndpoint || "/buyers/parent-companies");
  const parents = Array.isArray(data) ? data : [];
  const isNew = !values.parentCompanyId;
  return (
    <div className="space-y-2">
      <Select
        value={values.parentCompanyId || "__new__"}
        onValueChange={(v) => {
          if (v === "__new__") {
            setField("parentCompanyId", "");
            setField(field.name, "");
          } else {
            const p = parents.find((x) => String(x.parentCompanyId) === String(v));
            setField("parentCompanyId", v);
            setField(field.name, p ? p.parentCompany : "");
          }
        }}
      >
        <SelectTrigger data-testid={`field-${field.name}`} className="h-9 text-sm">
          <SelectValue placeholder="Select existing parent company" />
        </SelectTrigger>
        <SelectContent className="bg-white">
          <SelectItem value="__new__" data-testid="field-parentCompany-new-option">+ New parent company…</SelectItem>
          {parents.map((p) => (
            <SelectItem key={p.parentCompanyId} value={String(p.parentCompanyId)}>{p.parentCompany}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isNew && (
        <Input
          data-testid="field-parentCompany-new"
          placeholder="New parent company name"
          value={values[field.name] ?? ""}
          onChange={(e) => setField(field.name, e.target.value)}
          className="h-9 text-sm"
        />
      )}
    </div>
  );
}

export function EntityFormDialog({ open, onOpenChange, mode, singular, fields, initialValues, onSubmit, submitting }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasParentPicker = useMemo(() => fields.some((f) => f.type === "parentCompany"), [fields]);

  const defaults = useMemo(() => {
    const d = {};
    fields.forEach((f) => {
      d[f.name] = f.type === "checkbox" ? f.default ?? false : f.type === "number" ? "" : "";
    });
    return d;
  }, [fields]);

  useEffect(() => {
    if (open) {
      const base = { ...defaults };
      if (initialValues) {
        fields.forEach((f) => {
          if (initialValues[f.name] !== undefined && initialValues[f.name] !== null) {
            base[f.name] = f.type === "checkbox" ? !!initialValues[f.name] : initialValues[f.name];
          }
        });
        if (hasParentPicker) base.parentCompanyId = initialValues.parentCompanyId || "";
      }
      setValues(base);
      setErrors({});
    }
  }, [open, initialValues, fields, defaults, hasParentPicker]);

  const setField = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const validate = () => {
    const e = {};
    fields.forEach((f) => {
      if (f.required && (values[f.name] === "" || values[f.name] === undefined || values[f.name] === null)) {
        e[f.name] = "Required";
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSaveClick = () => {
    if (validate()) setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    const payload = {};
    fields.forEach((f) => {
      let v = values[f.name];
      if (f.type === "number") v = v === "" || v === null || v === undefined ? undefined : Number(v);
      if (f.type === "checkbox") v = !!v;
      if (v !== undefined) payload[f.name] = v;
    });
    if (hasParentPicker) payload.parentCompanyId = values.parentCompanyId || "";
    setConfirmOpen(false);
    await onSubmit(payload);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {mode === "edit" ? `Edit ${singular}` : `New ${singular}`}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Changes are saved directly to the live shared ERP backend.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            {fields.map((f, idx) => {
              const showSection = f.section && (idx === 0 || fields[idx - 1].section !== f.section);
              return (
                <Fragment key={f.name}>
                  {showSection && (
                    <div
                      className="sm:col-span-2 -mb-1 mt-2 border-b border-stone-200 pb-1 text-xs font-semibold uppercase tracking-wider text-amber-800"
                      data-testid={`form-section-${f.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {f.section}
                    </div>
                  )}
                  <div className={cn("space-y-1.5", f.col === 2 && "sm:col-span-2", f.type === "checkbox" && "flex items-center gap-2 space-y-0 pt-2")}>
                    {f.type === "checkbox" ? (
                      <>
                        <Checkbox
                          id={`f-${f.name}`}
                          data-testid={`field-${f.name}`}
                          checked={!!values[f.name]}
                          onCheckedChange={(c) => setField(f.name, !!c)}
                        />
                        <Label htmlFor={`f-${f.name}`} className="cursor-pointer">{f.label}</Label>
                      </>
                    ) : (
                      <>
                        <Label htmlFor={`f-${f.name}`}>
                          {f.label} {f.required && <span className="text-rose-500">*</span>}
                        </Label>
                        {f.type === "select" ? (
                          <DynamicSelect field={f} value={values[f.name]} onChange={(v) => setField(f.name, v)} />
                        ) : f.type === "parentCompany" ? (
                          <ParentCompanyField field={f} values={values} setField={setField} />
                        ) : f.type === "textarea" ? (
                          <Textarea
                            id={`f-${f.name}`}
                            data-testid={`field-${f.name}`}
                            value={values[f.name] ?? ""}
                            onChange={(e) => setField(f.name, e.target.value)}
                            className="min-h-[70px] text-sm"
                          />
                        ) : (
                          <Input
                            id={`f-${f.name}`}
                            data-testid={`field-${f.name}`}
                            type={f.type === "number" ? "number" : "text"}
                            placeholder={f.placeholder}
                            value={values[f.name] ?? ""}
                            onChange={(e) => setField(f.name, e.target.value)}
                            className="h-9 text-sm"
                          />
                        )}
                        {errors[f.name] && <p className="text-xs text-rose-500">{errors[f.name]}</p>}
                      </>
                    )}
                  </div>
                </Fragment>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Cancel</Button>
            <Button className="bg-amber-700 hover:bg-amber-800" onClick={handleSaveClick} disabled={submitting} data-testid="form-save">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "edit" ? "Save changes" : `Create ${singular}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" /> Confirm write to live ERP
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will {mode === "edit" ? "update" : "create"} a {singular.toLowerCase()} record in the shared production
              database, visible to both web and mobile apps. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-save" onClick={handleConfirm} className="bg-amber-700 hover:bg-amber-800">
              Yes, save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
